import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import React, { useEffect, useState } from "react";
import { Container } from "./styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination } from "components/helpers";
import { getPracticeScribingStatusSummary } from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import NotesCell from "./notesCell";
import SummaryDetails from "./summaryDetails";
import DictationSummary from "../dictationSummary";
import CountWithPercent from "./countWithPercentage";

const columns = (handleOpenDictationDialog, getDetails) => {
  return [
    {
      field: "provider_name",
      headerName: "Provider Name",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => handleOpenDictationDialog(params.row)}
          sx={{ textTransform: "none", justifyContent: "flex-start" }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: "dos",
      headerName: "DOS",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => handleOpenDictationDialog(params.row)}
          sx={{ textTransform: "none", justifyContent: "flex-start" }}
        >
          {params.value}
        </Button>
      ),
    },
    { field: "appt_count", headerName: "Appt Count", flex: 1 },
    {
      field: "adv_chart_completed",
      headerName: "Adv. Chart",
      flex: 1,
      renderCell: (params) => (
        <CountWithPercent
          completed={params.value}
          total={params.row.appt_count}
        />
      ),
    },
    {
      field: "total_dictations",
      headerName: "Total Dictations",
      flex: 1,
      renderCell: (params) => (
        <CountWithPercent
          completed={params.value}
          total={params.row.appt_count}
        />
      ),
    },
    {
      field: "scribing_completed",
      headerName: "Scribing",
      flex: 1,
      renderCell: (params) => (
        <CountWithPercent
          completed={params.value}
          total={params.row.total_dictations}
        />
      ),
    },
    {
      field: "coding_review_completed",
      headerName: "Coding Review",
      flex: 1,
      renderCell: (params) => (
        <CountWithPercent
          completed={params.value}
          total={params.row.total_dictations}
        />
      ),
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 3,
      sortable: false,
      renderCell: (params) => (
        <NotesCell
          row={params.row}
          onClickDetail={(type, row) => {
            getDetails(row.dos);
          }}
        />
      ),
    },
  ];
};

const Summary = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [open, setOpen] = useState(false);
  const [dos, setDos] = useState(null);

  const [openDictationDialog, setOpenDictationDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const userData = useUserData();
  const token = userData?.access_token;

  useEffect(() => {
    getScribingSummary();
  }, []);

  const getScribingSummary = async () => {
    setLoading(true);
    try {
      const payload = {
        type: "Dictation",
        search_criteria: {
          practice_id: userData?.selected_practice,
        },
      };

      const res = await getPracticeScribingStatusSummary(payload, token);

      if (res && res.length > 0) {
        setRows(res);
      } else {
        setRows([]);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = (rowDos) => {
    setOpen(true);
    setDos(rowDos);
  };

  const handleCloseDetails = () => {
    setOpen(false);
    setDos(null);
  };

  const handleOpenDictationDialog = (row) => {
    setSelectedRow(row);
    setOpenDictationDialog(true);
  };

  const handleCloseDictationDialog = () => {
    setOpenDictationDialog(false);
    setSelectedRow(null);
  };

  return (
    <Container>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={1.5}
      >
        <Box>
          <Typography fontSize={20} fontWeight={600}>
            Scribing Summary
          </Typography>

          {lastUpdated && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Last updated: {lastUpdated.toLocaleString()}
            </Typography>
          )}
        </Box>

        <Tooltip title="Refresh summary">
          <span>
            <IconButton
              onClick={getScribingSummary}
              disabled={loading}
              sx={{
                width: 42,
                height: 42,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
                backgroundColor: "background.paper",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "grey.100",
                  transform: "rotate(90deg)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} thickness={5} />
              ) : (
                <RefreshRoundedIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <DataGrid
        pageSize={25}
        density="compact"
        rows={rows}
        columns={columns(handleOpenDictationDialog, handleOpenDetails)}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        pagination
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        slots={{
          pagination: DataGridCustomPagination,
          toolbar: GridToolbar,
        }}
        getRowHeight={() => "auto"}
        getRowId={(row) => row.row_num}
      />

      <SummaryDetails
        open={open}
        onClose={handleCloseDetails}
        userData={userData}
        dos={dos}
      />

      <Dialog
        open={openDictationDialog}
        onClose={handleCloseDictationDialog}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Box>
            <Typography fontWeight={600}>
              {selectedRow?.provider_name || "Dictation Summary"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              DOS: {selectedRow?.dos || "-"}
            </Typography>
          </Box>

          <IconButton
            onClick={handleCloseDictationDialog}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedRow && (
            <DictationSummary
              userData={userData}
              dateOfService={selectedRow?.dos}
              row={selectedRow}
              npi={selectedRow?.npi}
              isDialog={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Summary;
